'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  canvasToBlob,
  clamp,
  formatFileSize,
  isRasterImageFile,
  loadRasterImage,
  sanitizeHexColor,
} from '@/lib/browser-image-tools';
import { drawFramedImageToCanvas, type CanvasFramingMode } from '@/lib/browser-png-tools';
import { encodeAnimatedWebp, getAnimationDuration, getBaseName } from '@/lib/browser-webp-tools';

type BackgroundMode = 'transparent' | 'white' | 'black' | 'custom';
type LoopMode = 'infinite' | 'once' | 'custom';

type FrameItem = {
  id: string;
  file: File;
  name: string;
  size: number;
  width: number;
  height: number;
  delayMs: number;
  previewUrl: string;
  element: HTMLImageElement;
};

function createFrameId(file: File, index: number) {
  return `${file.name}-${file.lastModified}-${index}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function BrowserAnimatedWebpMaker() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [frames, setFrames] = useState<FrameItem[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [framingMode, setFramingMode] = useState<CanvasFramingMode>('fit');
  const [defaultDelayMs, setDefaultDelayMs] = useState(120);
  const [loopMode, setLoopMode] = useState<LoopMode>('infinite');
  const [customLoopCount, setCustomLoopCount] = useState(3);
  const [backgroundMode, setBackgroundMode] = useState<BackgroundMode>('transparent');
  const [customHex, setCustomHex] = useState('#ffffff');
  const [qualityPreset, setQualityPreset] = useState<'high' | 'balanced' | 'smaller'>('balanced');
  const [resultSize, setResultSize] = useState<number | null>(null);

  const canvasSize = useMemo(() => {
    if (!frames.length) {
      return null;
    }

    return frames.reduce(
      (accumulator, frame) => ({
        width: Math.max(accumulator.width, frame.width),
        height: Math.max(accumulator.height, frame.height),
      }),
      { width: 1, height: 1 },
    );
  }, [frames]);

  const exportBackground = useMemo(() => {
    switch (backgroundMode) {
      case 'white':
        return '#ffffff';
      case 'black':
        return '#000000';
      case 'custom':
        return sanitizeHexColor(customHex);
      default:
        return null;
    }
  }, [backgroundMode, customHex]);

  const sourceDuration = useMemo(
    () => getAnimationDuration(frames.map((frame) => clamp(Math.round(frame.delayMs), 20, 5000))),
    [frames],
  );

  useEffect(() => {
    return () => {
      frames.forEach((frame) => URL.revokeObjectURL(frame.previewUrl));
      [previewUrl, downloadUrl].forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [downloadUrl, frames, previewUrl]);

  function resetOutputState() {
    [previewUrl, downloadUrl].forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });

    setPreviewUrl(null);
    setDownloadUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  async function handleFileSelection(fileList: FileList | File[] | null) {
    setErrorMessage(null);
    setCreditMessage(null);

    const selectedFiles = fileList ? Array.from(fileList) : [];

    if (!selectedFiles.length) {
      return;
    }

    const rasterFiles = selectedFiles.filter(isRasterImageFile);

    if (!rasterFiles.length) {
      setErrorMessage('Choose PNG, JPG, or WebP frames. Non-image files were skipped.');
      return;
    }

    try {
      const nextFrames = await Promise.all(
        rasterFiles.map(async (file, index) => {
          const loadedImage = await loadRasterImage(file);

          return {
            id: createFrameId(file, index),
            file,
            name: file.name,
            size: file.size,
            width: loadedImage.width,
            height: loadedImage.height,
            delayMs: defaultDelayMs,
            previewUrl: URL.createObjectURL(file),
            element: loadedImage.element,
          } satisfies FrameItem;
        }),
      );

      resetOutputState();
      setFrames((currentFrames) => [...currentFrames, ...nextFrames]);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The source frames could not be added.',
      );
    }
  }

  function updateFrameDelay(frameId: string, delayMs: number) {
    resetOutputState();
    setFrames((currentFrames) =>
      currentFrames.map((frame) =>
        frame.id === frameId ? { ...frame, delayMs: clamp(delayMs, 20, 5000) } : frame,
      ),
    );
  }

  function moveFrame(frameId: string, direction: -1 | 1) {
    resetOutputState();
    setFrames((currentFrames) => {
      const currentIndex = currentFrames.findIndex((frame) => frame.id === frameId);
      const nextIndex = currentIndex + direction;

      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= currentFrames.length) {
        return currentFrames;
      }

      const reordered = [...currentFrames];
      const [frame] = reordered.splice(currentIndex, 1);
      reordered.splice(nextIndex, 0, frame);
      return reordered;
    });
  }

  function removeFrame(frameId: string) {
    resetOutputState();
    setFrames((currentFrames) => {
      const frameToRemove = currentFrames.find((frame) => frame.id === frameId);

      if (frameToRemove) {
        URL.revokeObjectURL(frameToRemove.previewUrl);
      }

      return currentFrames.filter((frame) => frame.id !== frameId);
    });
  }

  async function runGeneration() {
    if (frames.length < 2 || !canvasSize) {
      setErrorMessage('Add at least 2 frames to build an animated WebP.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'animated-webp-maker',
        'Credits are not available for another animated WebP export right now.',
      );
      const preparedFrames = await Promise.all(
        frames.map(async (frame) => {
          const canvas = drawFramedImageToCanvas(
            frame.element,
            canvasSize.width,
            canvasSize.height,
            framingMode,
            exportBackground,
          );
          return {
            blob: await canvasToBlob(canvas, 'image/png'),
            delayMs: clamp(Math.round(frame.delayMs), 20, 5000),
          };
        }),
      );
      const loopCount =
        loopMode === 'infinite' ? 0 : loopMode === 'once' ? 1 : clamp(customLoopCount, 1, 999);
      const quality = qualityPreset === 'high' ? 88 : qualityPreset === 'smaller' ? 62 : 76;
      const method = qualityPreset === 'high' ? 4 : qualityPreset === 'smaller' ? 6 : 5;
      const webpBlob = await encodeAnimatedWebp({
        frames: preparedFrames,
        loopCount,
        quality,
        method,
      });
      const nextPreviewUrl = URL.createObjectURL(webpBlob);
      const nextDownloadUrl = URL.createObjectURL(webpBlob);

      resetOutputState();
      setPreviewUrl(nextPreviewUrl);
      setDownloadUrl(nextDownloadUrl);
      setDownloadName(`${getBaseName(frames[0].name)}.webp`);
      setResultSize(webpBlob.size);
      setCreditMessage(formatToolCreditSuccess('Animated WebP export', credits));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The animated WebP export failed.');
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Build an animated WebP from image frames</h2>
          <p>
            Upload PNG, JPG, or WebP frames, set order and timing, and export one animated WebP
            locally in the browser.
          </p>
        </div>
        <span className="tool-live-badge">WebP</span>
      </div>

      <div className="tool-live-routes" aria-label="Canvas framing">
        <button
          className={`tool-live-route ${framingMode === 'fit' ? 'tool-live-route-active' : ''}`}
          onClick={() => {
            setFramingMode('fit');
            resetOutputState();
          }}
          type="button"
        >
          Fit
        </button>
        <button
          className={`tool-live-route ${framingMode === 'fill' ? 'tool-live-route-active' : ''}`}
          onClick={() => {
            setFramingMode('fill');
            resetOutputState();
          }}
          type="button"
        >
          Fill
        </button>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files);
            }}
            type="button"
          >
            <div>
              <strong>
                {frames.length
                  ? `${frames.length} frame${frames.length === 1 ? '' : 's'} loaded`
                  : 'Choose image frames or drop them here'}
              </strong>
              <span>
                {frames.length
                  ? `${formatFileSize(frames.reduce((sum, frame) => sum + frame.size, 0))} total`
                  : 'PNG, JPG, and WebP frames are supported'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/png,image/jpeg,image/webp"
            className="tool-live-file-input"
            multiple
            onChange={(event) => void handleFileSelection(event.target.files)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Default delay</span>
              <input
                min="20"
                onChange={(event) =>
                  setDefaultDelayMs(Math.max(20, Number(event.target.value) || 120))
                }
                type="number"
                value={defaultDelayMs}
              />
            </label>
            <label className="tool-live-field">
              <span>Loop mode</span>
              <select
                onChange={(event) => {
                  setLoopMode(event.target.value as LoopMode);
                  resetOutputState();
                }}
                value={loopMode}
              >
                <option value="infinite">Infinite</option>
                <option value="once">Play once</option>
                <option value="custom">Custom</option>
              </select>
            </label>
          </div>

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Background</span>
              <select
                onChange={(event) => {
                  setBackgroundMode(event.target.value as BackgroundMode);
                  resetOutputState();
                }}
                value={backgroundMode}
              >
                <option value="transparent">Transparent</option>
                <option value="white">White</option>
                <option value="black">Black</option>
                <option value="custom">Custom</option>
              </select>
            </label>
            {backgroundMode === 'custom' ? (
              <label className="tool-live-field">
                <span>Custom color</span>
                <input
                  onChange={(event) => {
                    setCustomHex(sanitizeHexColor(event.target.value));
                    resetOutputState();
                  }}
                  type="text"
                  value={customHex}
                />
              </label>
            ) : null}
            {loopMode === 'custom' ? (
              <label className="tool-live-field">
                <span>Loop count</span>
                <input
                  min="1"
                  onChange={(event) => {
                    setCustomLoopCount(Math.max(1, Number(event.target.value) || 1));
                    resetOutputState();
                  }}
                  type="number"
                  value={customLoopCount}
                />
              </label>
            ) : null}
          </div>

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Quality preset</span>
              <select
                onChange={(event) => {
                  setQualityPreset(event.target.value as 'high' | 'balanced' | 'smaller');
                  resetOutputState();
                }}
                value={qualityPreset}
              >
                <option value="high">High quality</option>
                <option value="balanced">Balanced</option>
                <option value="smaller">Smaller file</option>
              </select>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={frames.length < 2 || isProcessing}
              onClick={() => void runGeneration()}
              type="button"
            >
              {isProcessing ? 'Building…' : 'Build animated WebP'}
            </button>
            {downloadUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={downloadUrl}>
                Download WebP
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Animation summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Frames</strong>
              <span>{frames.length || 'Add frames first'}</span>
            </div>
            <div>
              <strong>Canvas</strong>
              <span>
                {canvasSize ? `${canvasSize.width} × ${canvasSize.height}` : 'Auto from frames'}
              </span>
            </div>
            <div>
              <strong>Source duration</strong>
              <span>
                {frames.length ? `${(sourceDuration / 1000).toFixed(2)}s` : 'Not available yet'}
              </span>
            </div>
            <div>
              <strong>Output size</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
          </div>
        </aside>
      </div>

      {frames.length ? (
        <div className="tool-live-preview">
          <h3>Frame order</h3>
          <div className="tool-apng-frame-list">
            {frames.map((frame, index) => (
              <div className="tool-apng-frame-card" key={frame.id}>
                <div className="tool-apng-frame-thumb">
                  <img alt={`${frame.name} preview`} src={frame.previewUrl} />
                </div>
                <div className="tool-apng-frame-copy">
                  <strong>{frame.name}</strong>
                  <span>{`${frame.width} × ${frame.height} • ${formatFileSize(frame.size)}`}</span>
                </div>
                <div className="hero-actions tool-live-actions">
                  <label className="tool-live-field">
                    <span>Delay</span>
                    <input
                      min="20"
                      onChange={(event) =>
                        updateFrameDelay(frame.id, Number(event.target.value) || 120)
                      }
                      type="number"
                      value={frame.delayMs}
                    />
                  </label>
                  <button
                    className="button button-ghost"
                    disabled={index === 0}
                    onClick={() => moveFrame(frame.id, -1)}
                    type="button"
                  >
                    Up
                  </button>
                  <button
                    className="button button-ghost"
                    disabled={index === frames.length - 1}
                    onClick={() => moveFrame(frame.id, 1)}
                    type="button"
                  >
                    Down
                  </button>
                  <button
                    className="button button-ghost"
                    onClick={() => removeFrame(frame.id)}
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="tool-live-preview">
        <h3>Preview</h3>
        <div className="tool-live-preview-frame">
          {previewUrl ? (
            <img alt="Animated WebP preview" src={previewUrl} />
          ) : (
            <div className="tool-editor-empty">
              <strong>Animated WebP preview</strong>
              <span>Build the WebP animation to preview it here.</span>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
